import React, { useState, useRef, useCallback, memo } from "react";
import Nav from "../components/Nav";
import { useNavContext } from "../context/NavContext";
import Breadcrumb from "../components/Breadcrumb";

export default function CartaPoder() {
  // ===== Estado =====
  const [ciudad, setCiudad] = useState("Guadalajara, Jal.");
  const [fechaLarga, setFechaLarga] = useState("23 de Julio del 2025");
  const [otorgante, setOtorgante] = useState("EPIGMENIO HERNÁNDEZ MAGNO");
  const [apoderado, setApoderado] = useState(
    "Sr. JOSÉ CARLOS CAMPOS RODRIGUEZ"
  );
  const [numeroGuia, setNumeroGuia] = useState("CY289449796DE");
  const [firmaIzq, setFirmaIzq] = useState("Hernández Magno");
  const [firmaDer, setFirmaDer] = useState("José Carlos Campos Rodriguez");
  const [isEditing, setIsEditing] = useState(true);

  const logoSrc = "/img/logo-comercial.svg";
  const { proximasAVencer, proximosEventos } = useNavContext();

  const GAP = 20;

  // ===== Control fino y estilos locales =====
  const SIGNATURES_MARGIN_TOP = 320;
  const outer = {
    background: "#f5f7fa",
    minHeight: "100dvh",
    paddingBlock: GAP,
  };

  const page = {
    maxWidth: 1220,
    margin: `${GAP}px auto`, // ↑↓ = 20px, ←→ = auto (centrado)
    background: "#fff",
    padding: "48px 72px 56px",
    color: "#111",
    lineHeight: 1.6,
    boxShadow: "0 2px 16px rgba(0,0,0,0.06)",
    minHeight: 1280,
    display: "flex",
    flexDirection: "column",
  };

  const content = {
    maxWidth: 980,
    margin: "0 auto",
    display: "flex",
    flexDirection: "column",
    flex: 1,
    minHeight: "100%",
  };
  const header = {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    marginBottom: 18,
  };
  const cityDate = { textAlign: "right", fontSize: 20, fontWeight: 700 };
  const light = { fontWeight: 600 };

  // Línea azul completa (pantalla)
  const blueRuleRow = { display: "block", marginTop: 16, marginBottom: 8 };
  const blueRule = {
    height: 8,
    width: "100%",
    background: "#0A6CA9",
    borderRadius: 2,
  };

  const hTitle = {
    textAlign: "center",
    margin: "30px 0 40px",
    fontSize: 30,
    letterSpacing: 0.3,
    fontWeight: 800,
  };
  const atn = { marginTop: 20, marginBottom: 6, fontSize: 20, fontWeight: 700 };
  const p = {
    fontSize: 22,
    marginTop: 20,
    marginBottom: 6,
    textAlign: "justify",
    hyphens: "auto",
  };

  // Bloque de firmas
  const firmaBlock = {
    marginTop: SIGNATURES_MARGIN_TOP,
    display: "flex",
    justifyContent: "space-between",
    padding: "0 16px",
    gap: 24,
  };

  // Cada columna centrada
  const firmCol = {
    width: "46%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
  };

  const firmName = {
    fontSize: 24,
    fontWeight: 800,
    marginBottom: 8,
    width: "100%",
    textAlign: "center",
  };
  const firmRole = {
    fontSize: 20,
    textAlign: "center",
    marginTop: 4,
    width: "100%",
  };

  const footerBarsWrap = {
    marginTop: 0,
    display: "flex",
    width: "100%",
    height: 22,
  };
  const footerBlue = { background: "#0A6CA9", width: "60%" };
  const footerDark = { background: "#3D3D3D", width: "40%" };

  // ===== Editable =====
  const Editable = memo(
    function Editable({
      enabled,
      initialText,
      onCommit,
      underline = false,
      bold = false,
      ariaLabel,
      style,
      block = false,
      color,
    }) {
      const ref = useRef(null);
      const inited = useRef(false);
      const [focused, setFocused] = useState(false);

      const setNode = useCallback(
        (node) => {
          if (!node) return;
          ref.current = node;
          if (!inited.current) {
            node.textContent = initialText || "";
            inited.current = true;
          }
        },
        [initialText]
      );

      const handleBlur = useCallback(() => {
        setFocused(false);
        if (onCommit && ref.current) onCommit(ref.current.textContent || "");
      }, [onCommit]);

      const handleFocus = useCallback(() => setFocused(true), []);

      const handleKeyDown = useCallback(
        (e) => {
          if (!block && e.key === "Enter") e.preventDefault();
        },
        [block]
      );

      const handlePaste = useCallback(
        (e) => {
          if (!enabled) return;
          e.preventDefault();
          const text = (e.clipboardData || window.clipboardData).getData(
            "text"
          );
          document.execCommand("insertText", false, text);
        },
        [enabled]
      );

      // Paleta rojo suave
      const redBase = "#ef4444";
      const redLight = "#fca5a5";
      const redBg = "#fef2f2";

      const editableBox = enabled
        ? {
            background: redBg,
            border: `1px dashed ${focused ? redBase : redLight}`,
            boxShadow: focused ? "0 0 0 2px rgba(239,68,68,.10)" : "none",
            borderRadius: 6,
            transition: "box-shadow .15s ease, border-color .15s ease",
          }
        : {};

      const sizing =
        enabled && block
          ? { display: "block", padding: "4px 6px", minHeight: 28 }
          : enabled
          ? { display: "inline-block", padding: "0 4px", lineHeight: 1.2 }
          : { display: block ? "inline-block" : "inline" };

      return (
        <span
          ref={setNode}
          role="textbox"
          aria-label={ariaLabel}
          contentEditable={enabled}
          suppressContentEditableWarning
          spellCheck={false}
          className={`cp-editable${block ? " cp-block" : ""}`}
          onBlur={enabled ? handleBlur : undefined}
          onFocus={enabled ? handleFocus : undefined}
          onKeyDown={enabled ? handleKeyDown : undefined}
          onPaste={enabled ? handlePaste : undefined}
          style={{
            boxSizing: "border-box",
            textDecoration: underline ? "underline" : "none",
            fontWeight: bold ? 800 : undefined,
            color: color || "inherit",
            cursor: enabled ? "text" : "default",
            verticalAlign: "baseline",
            ...sizing,
            ...editableBox,
            ...style,
          }}
        />
      );
    },
    // Re-renderiza cuando cambian props clave (ej. enabled) para reflejar bordes/estilos
    (prev, next) =>
      prev.enabled === next.enabled &&
      prev.block === next.block &&
      prev.underline === next.underline &&
      prev.bold === next.bold &&
      prev.color === next.color
  );

  // ===== Imprimir =====
  const handlePrint = () => {
    const wasEditing = isEditing;
    setIsEditing(false);

    requestAnimationFrame(() => {
      const pageNode = document.querySelector(".cp-page");
      if (!pageNode) return;

      const html = `
<!doctype html>
<html>
<head>
<meta charset="utf-8" />
<title>Carta Poder</title>
<style>
  /* Control total de márgenes con el contenido */
  @page { size: A4; margin: 0; }

  * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  html, body {
    height: 100%;
    margin: 0;
    background: #fff;
    color:#111; line-height:1.5; font-size:13.5px;
    font-family: Arial, Helvetica, sans-serif;
  }

  /* 1 sola hoja */
  .print-sheet{ position:relative; height:297mm; overflow:hidden; }

  .cp-page{
    box-shadow:none !important;
    margin:0 !important;
    padding:0 !important;
    background:#fff !important;
    height:297mm;              /* <- fuerza una única página */
    overflow:hidden;           /* <- evita que cree otra */
    display:flex; flex-direction:column;
  }

  .cp-content{
    max-width:none !important;
    margin:6mm 12mm 12mm !important; /* 6mm arriba, 12mm lados/abajo */
    padding:0 0 52px 0 !important;   /* respiro para el footer fijo */
    box-sizing:border-box;
    display:flex; flex-direction:column; min-height:100%;
  }

  /* El spacer de pantalla aquí estorba */
  .cp-spacer{ display:none !important; }

  /* Limpia los bordes de edición */
  .cp-editable{
    background:transparent !important;
    border:none !important;
    box-shadow:none !important;
    padding:0 !important;
    display:inline;
  }
  .cp-editable.cp-block{ display:inline; }

  /* Estilos de impresión ya tuyos */
  .cp-logo{ height:56px; }
  .cp-bluebar{ width:100%; height:8px; background:#0A6CA9; border-radius:2px; }
  h2{ font-size:26px; margin:22px 0 28px; letter-spacing:.3px; font-weight:800; text-align:center; }
  .cp-atn{ font-size:16px; margin-top:16px; margin-bottom:4px; }
  .cp-p{ font-size:14px; line-height:1.55; margin-top:14px; margin-bottom:6px; }
  .cp-citydate{ font-size:16px; }
  .cp-firm-name{ font-size:18px; }
  .cp-firm-role{ font-size:14px; }
  .cp-firmas{ break-inside:avoid; page-break-inside:avoid; }

  /* Footer fijo, ancho completo entre márgenes */
  .footer-bars{
    position:fixed;
    inset:auto 0 0 0;
    height:22px;
    display:flex;
    margin:0 !important;
    z-index:9999;
  }
  .footer-blue{ background:#0A6CA9; width:60%; }
  .footer-dark{ background:#3D3D3D; width:40%; }
</style>
</head>
<body>
  <div class="print-sheet">${pageNode.outerHTML}</div>
</body>
</html>`.trim();

      // Imprimir en iframe oculto
      const iframe = document.createElement("iframe");
      Object.assign(iframe.style, {
        position: "fixed",
        right: 0,
        bottom: 0,
        width: "0",
        height: "0",
        border: 0,
      });
      document.body.appendChild(iframe);

      const doc = iframe.contentDocument || iframe.contentWindow.document;
      doc.open();
      doc.write(html);
      doc.close();

      let finished = false;
      const cleanup = () => {
        if (finished) return;
        finished = true;
        setIsEditing(wasEditing);
        setTimeout(() => iframe.remove(), 0);
      };

      iframe.onload = () => {
        setTimeout(() => {
          iframe.contentWindow.focus();
          iframe.contentWindow.print();
        }, 0);
      };
      iframe.contentWindow.addEventListener("afterprint", cleanup, {
        once: true,
      });
      setTimeout(cleanup, 5000);
    });
  };

  return (
    <>
      <Nav
        handleSearch={() => {}}
        proximasAVencer={proximasAVencer}
        proximosEventos={proximosEventos}
      />

      {/* ── Header compacto sin línea y alineado ── */}
      <div
        className="task-filters"
        style={{
          display: "flex",
          alignItems: "center",
          flexWrap: "nowrap", // todo en una sola fila
          gap: 8,
          borderBottom: "none", // quita la línea
        }}
      >
        {/* Izquierda: Breadcrumb */}
        <div
          style={{
            flex: 1,
            padding: "0 4px",
            minHeight: 36, // mismo alto visual que tus headers
            display: "flex",
            alignItems: "center",
          }}
        >
          <Breadcrumb
            aliases={{
              personalizacion: "Personalizacion",
              "carta-poder": "Carta Poder",
            }}
          />
        </div>

        {/* Derecha: Botones */}
        <div
          className="task-filters-right"
          style={{ display: "flex", alignItems: "center", gap: 10 }}
        >
          <button
            className="exportar-button"
            onClick={handlePrint}
            title="Imprimir"
          >
            Imprimir
          </button>
          <button
            className="new-task-button"
            onClick={() => setIsEditing((v) => !v)}
            title={isEditing ? "Guardar" : "Editar"}
          >
            {isEditing ? "Guardar" : "Editar"}
          </button>
        </div>
      </div>

      <div style={outer}>
        {/* ===== Página ===== */}
        <div style={page} className="cp-page">
          <div style={content} className="cp-content">
            {/* Cabecera */}
            <div style={header}>
              <img
                src={logoSrc}
                alt="Logo"
                style={{ height: 76 }}
                className="cp-logo"
              />
              <div style={cityDate} className="cp-citydate">
                <div>
                  <Editable
                    enabled={isEditing}
                    initialText={ciudad}
                    onCommit={setCiudad}
                    ariaLabel="Ciudad"
                    block
                  />
                </div>
                <div style={{ fontSize: 20, fontWeight: 700 }}>
                  <Editable
                    enabled={isEditing}
                    initialText={fechaLarga}
                    onCommit={setFechaLarga}
                    ariaLabel="Fecha"
                    block
                  />
                </div>
              </div>
            </div>

            {/* Línea azul única */}
            <div style={blueRuleRow}>
              <div style={blueRule} className="cp-bluebar" />
            </div>

            {/* Título */}
            <h2 style={hTitle}>CARTA PODER</h2>

            {/* ATN */}
            <div style={atn} className="cp-atn">
              ATN:&nbsp;<span style={light}>A QUIÉN CORRESPONDA,</span>
            </div>

            {/* Cuerpo */}
            <p style={p} className="cp-p">
              Por la presente, yo{" "}
              <Editable
                enabled={isEditing}
                initialText={otorgante}
                onCommit={setOtorgante}
                ariaLabel="Otorgante"
                underline
                bold
              />{" "}
              doy al{" "}
              <Editable
                enabled={isEditing}
                initialText={apoderado}
                onCommit={setApoderado}
                ariaLabel="Apoderado"
                underline
                bold
              />{" "}
              poder amplio, cumplido y bastante, para que a mi nombre y
              representación, reciba el paquete con número de guía:&nbsp;
              <Editable
                enabled={isEditing}
                initialText={numeroGuia}
                onCommit={setNumeroGuia}
                ariaLabel="Número de guía"
                underline
                bold
                color="#111"
              />
              .
            </p>

            <p style={p} className="cp-p">
              Sin más por el momento agradezco su ayuda y quedo de usted para
              cualquier aclaración.
            </p>

            {/* Firmas */}
            <div style={firmaBlock} className="cp-firmas">
              <div style={firmCol}>
                <div style={firmName} className="cp-firm-name">
                  <Editable
                    enabled={isEditing}
                    initialText={firmaIzq}
                    onCommit={setFirmaIzq}
                    ariaLabel="Nombre firma izquierda"
                    bold
                  />
                </div>
                <div style={firmRole} className="cp-firm-role">
                  Representante Legal
                </div>
              </div>

              <div style={firmCol}>
                <div style={firmName} className="cp-firm-name">
                  <Editable
                    enabled={isEditing}
                    initialText={firmaDer}
                    onCommit={setFirmaDer}
                    ariaLabel="Nombre firma derecha"
                    bold
                  />
                </div>
                <div style={firmRole} className="cp-firm-role">
                  Acepto el Poder
                </div>
              </div>
            </div>

            {/* Spacer visual */}
            <div style={{ flexGrow: 1 }} className="cp-spacer" />

            {/* Barras pie */}
            <div style={footerBarsWrap} className="footer-bars">
              <div style={footerBlue} className="footer-blue" />
              <div style={footerDark} className="footer-dark" />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
